import { apiClient } from '@/services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UserInfo {
  sub: string;
  email: string;
  displayName?: string;
  name?: string;
  username?: string;
}

// ── Role Display Mapping (work.txt §2.3 / workfinal.txt §3.3) ────────────────
// manager is now a real, separate role (see SYSTEM_ROLES) — org_admin's
// label reverted to avoid the two colliding on the same label.
export const ROLE_DISPLAY_MAP: Record<string, string> = {
  platform_admin: 'Platform Admin',
  org_admin: 'Organization Administrator',
  manager: 'Manager',
  tier3: 'Tier 3 Analyst',
  tier2: 'Tier 2 Analyst',
  tier1: 'Tier 1 Analyst',
};

export function displayRoleName(backendRole: string): string {
  return ROLE_DISPLAY_MAP[backendRole] || backendRole.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export interface CaseComment {
  id: string;
  caseId: string;
  authorId: string;
  parentCommentId?: string | null;
  body: string;
  isInternal: boolean;
  mentions: string;
  createdAt: string;
  editedAt?: string | null;
  deletedAt?: string | null;
  author?: UserInfo;
}

export type AlertCorrelationCriteria =
  | 'MALWARE_FAMILY'
  | 'PHISHING_CAMPAIGN'
  | 'THREAT_ACTOR'
  | 'ROOT_CAUSE'
  | 'INVESTIGATOR'
  | 'OTHER';

export interface LinkCaseAlertDto {
  alertId: string;
  isPrimary?: boolean;
  justification?: string;
  correlationCriteria?: AlertCorrelationCriteria;
}

export interface CaseTask {
  id: string;
  caseId: string;
  title: string;
  description?: string | null;
  status: string;
  taskType?: string;
  linkedCaseStatus?: string | null;
  isDefault?: boolean;
  boardOrder?: number;
  category?: string | null;
  categories?: string | string[];
  categoryLabels?: string[];
  assignedTo?: string | null;
  assignedBy?: string | null;
  assignedAt?: string | null;
  reassignedAt?: string | null;
  createdBy: string;
  dueAt?: string | null;
  reminderAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  estimatedHours?: number | null;
  actualHours?: number | null;
  blockedReason?: string | null;
  priority: number;
  priorityLabel?: string;
  createdAt: string;
  updatedAt: string;
  isOverdue?: boolean;
  remainingTime?: string | null;
  case?: { id: string; caseNumber: string; title: string; status: string };
  creator?: UserInfo;
  assignee?: UserInfo;
  assignedByUser?: UserInfo;
  items?: CaseTaskItem[];
}

export interface CaseTaskItem {
  id: string;
  taskId: string;
  title: string;
  isChecked: boolean;
  checkedAt?: string | null;
  checkedBy?: string | null;
  boardOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TaskTemplateTaskItem {
  id: string;
  taskId: string;
  title: string;
  boardOrder: number;
}

export interface TaskTemplateTask {
  id: string;
  templateId: string;
  title: string;
  description: string;
  linkedCaseStatus: string;
  boardOrder: number;
  items: TaskTemplateTaskItem[];
}

export interface TaskTemplate {
  id: string;
  organizationId: string | null;
  name: string;
  incidentType: string;
  description: string;
  isBuiltIn: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  tasks: TaskTemplateTask[];
}

export interface OperationalTask extends CaseTask {
  categories: string[];
  categoryLabels: string[];
  priorityLabel: string;
  isOverdue: boolean;
  remainingTime: string | null;
  case: { id: string; caseNumber: string; title: string; status: string };
}

export interface TaskDashboardSummary {
  myOpenTasks: number;
  dueToday: number;
  overdue: number;
  completedToday: number;
  nextDueAt: string | null;
  nextDueTitle: string | null;
  dueThisWeek: number;
  averageCompletionTimeHours: number | null;
  completionRate: number | null;
  tasksByPriority: Array<{ priority: number; label: string; count: number }>;
  tasksByCategory: Array<{ category: string; count: number }>;
}

export interface GetOperationalTasksQuery {
  search?: string;
  status?: string;
  priority?: number;
  category?: string;
  assignedTo?: string;
  caseId?: string;
  dueBefore?: string;
  dueAfter?: string;
  overdue?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

export interface GetMyTasksQuery {
  filter?: 'overdue' | 'due_today' | 'in_progress' | 'completed' | 'blocked';
  status?: string;
  priority?: number;
  caseId?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

export interface TimelineEvent {
  id: string;
  caseId: string;
  actorId?: string | null;
  eventType: string;
  eventAt: string;
  summary: string;
  sourceIp?: string | null;
  actor?: UserInfo;
}

export interface Alert {
  id: string;
  organizationId: string;
  sourceId: string;
  source?: { sourceName: string };
  externalAlertId: string;
  alertName: string;
  alertBody: string;
  rawPayload?: string | null;
  severity: string;
  status: string;
  attackType: string;
  mitreTactic?: string | null;
  mitreTechnique?: string | null;
  ruleId?: string | null;
  sourceIp?: string | null;
  destinationIp?: string | null;
  sourcePort?: number | null;
  destinationPort?: number | null;
  protocol?: string | null;
  appProtocol?: string | null;
  attackOrigin?: string | null;
  assetName?: string | null;
  assetCriticality?: string | null;
  username?: string | null;
  httpStatus?: number | null;
  alertTime: string;
  receivedAt: string;
  assignedToSub?: string | null;
  acknowledgedBySub?: string | null;
  acknowledgedAt?: string | null;
  closedBySub?: string | null;
  closedAt?: string | null;
  falsePositive?: boolean | null;
  falsePositiveReason?: string | null;
  enrichment?: string | null;
  confidenceScore?: number | null;
  fingerprint?: string | null;
  occurrenceCount?: number;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  // Included when querying escalated/linked alerts
  caseAlerts?: Array<{
    caseId: string;
    isPrimary: boolean;
    linkedAt: string;
    linkReason?: string | null;
    case: { id: string; caseNumber: string; title: string; status: string };
  }>;
}

export interface AlertObservable {
  id: string;
  alertId: string;
  organizationId: string;
  type: string;
  value: string;
  createdAt: string;
}

export interface MatchedObservableEvidence {
  type: string;
  value: string;
  weight: number;
  tier: string;
  reason?: string;
}

export interface SimilarCase {
  id: string;
  caseNumber: string;
  title: string;
  status: string;
  severity: string;
  priority: number | string;
  assignedTo?: string | null;
  sourceIp?: string | null;
  destinationIp?: string | null;
  affectedAsset?: string | null;
  caseType?: string | null;
  primaryCategory?: string | null;
  createdAt: string;
  score?: number;
  matchedObservables?: MatchedObservableEvidence[];
  scoreExplanation?: string;
  calculationVersion?: string;
}

export interface SimilarAlert {
  id: string;
  similarityId?: string;
  alertName: string;
  externalAlertId?: string;
  severity: string;
  status: string;
  attackType: string;
  ruleId?: string | null;
  sourceIp?: string | null;
  destinationIp?: string | null;
  assetName?: string | null;
  occurrenceCount?: number;
  alertTime: string;
  receivedAt: string;
  source?: { sourceName: string; vendor: string };
  score?: number;
  matchedObservables?: MatchedObservableEvidence[];
  scoreExplanation?: string;
  correlationStatus?: string;
  calculationVersion?: string;
}

export interface EscalateAlertDto {
  title?: string;
  description?: string;
  severity?: string;
  priority?: number;
  caseType?: string;
}

export interface AlertEnrichment {
  id: string;
  alertId: string;
  analyzerName: string;
  payload: string;
  score?: number | null;
  createdAt: string;
}

export interface CaseAlert {
  caseId: string;
  alertId: string;
  linkedBySub: string;
  linkReason?: string | null;
  linkedAt: string;
  isPrimary: boolean;
  alert?: Alert;
}

export interface Observable {
  id: string;
  caseId: string;
  organizationId: string;
  type: string; // IP, DOMAIN, URL, EMAIL, USERNAME, HOSTNAME, FILE_HASH
  value: string;
  enrichmentData?: Record<string, {
    provider: string;
    verdict: 'malicious' | 'suspicious' | 'clean' | 'unknown';
    score: number;
    headline: string;
    summary: string;
    details: Record<string, any>;
    rawResponse?: any;
    checkedAt: string;
    checkedBy?: string;
  }> | null;
  lastEnrichedAt?: string | null;
  lastEnrichedBy?: string | null;
  createdAt: string;
}

export interface Evidence {
  id: string;
  caseId: string;
  organizationId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  storagePath: string;
  uploadedBy: string;
  createdAt: string;
}

export interface CaseMitreMapping {
  caseId: string;
  technique: string;       // e.g. T1558.001
  tactic: string;          // e.g. credential-access
  techniqueName?: string;  // e.g. Golden Ticket
  tacticTitle?: string;    // e.g. Credential Access
  occurDate?: string | null;
  procedure?: string | null;
  createdBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AddMitreMappingDto {
  technique: string;
  tactic: string;
  techniqueName?: string;
  tacticTitle?: string;
  occurDate?: string;
  procedure?: string;
}

export interface CertCooperationAccess {
  id: string;
  caseId: string;
  organizationId: string;
  tokenHash: string;
  grantedBy: string;
  expiresAt: string;
  revokedAt?: string | null;
  revokedBy?: string | null;
  revocationReason?: string | null;
  accessLevel: string; // READ_ONLY, COMMENT, CONTRIBUTE
  reason?: string | null;
  requestedBy?: string | null;
}

export interface CaseSlaPause {
  id: string;
  caseId: string;
  pausedBy: string;
  pauseReason?: string | null;
  pausedAt: string;
  resumedAt?: string | null;
  totalMinutes?: number | null;
}

export interface CaseAssignmentHistory {
  id: string;
  caseId: string;
  fromUserId?: string | null;
  toUserId: string;
  assignedBy: string;
  reason?: string | null;
  assignedAt: string;
}

export interface CaseEscalationHistory {
  id: string;
  caseId: string;
  fromRole: string;
  toRole: string;
  escalatedBy: string;
  escalationReason?: string | null;
  createdAt: string;
}

export interface AnalystAvailability {
  userId: string;
  onShift: boolean;
  available: boolean;
  currentWorkload: number;
  shiftStart?: string | null;
  shiftEnd?: string | null;
}

export interface EscalationKpis {
  totalCases: number;
  escalatedCasesCount: number;
  escalationRate: number;
  tier1ToTier2Count: number;
  tier2ToTier3Count: number;
  averageResponseTimeMinutes: number;
}

export interface CertIncidentReport {
  id: string;
  caseId: string;
  organizationId: string;
  status: string; // PENDING_SEND, SENT, FAILED
  sentAt?: string | null;
  payload: string;
}

export interface AssignmentRecommendation {
  sub: string;
  email: string;
  displayName?: string;
  username?: string;
  role: string;
  workloadScore: number;
  activeCases: number;
  isRecommended?: boolean;
  onShift?: boolean;
  available?: boolean;
}

export interface CaseAssignment {
  id: string;
  caseId: string;
  analystId: string;
  role: 'LEAD' | 'ANALYST' | string;
  assignedBy: string;
  assignedAt: string;
  removedAt?: string | null;
  analyst?: UserInfo;
}

export interface Case {
  id: string;
  organizationId: string;
  caseNumber: string;
  title: string;
  description: string;
  status: string;
  severity: string;
  priority: number;
  caseType: string;
  sourceIp?: string | null;
  destinationIp?: string | null;
  httpStatus?: string | null;
  attackOrigin?: string | null;
  affectedAsset?: string | null;
  shift?: string | null;
  impact?: string | null;
  recommendation?: string | null;
  assignedTo?: string | null;
  createdBy: string;
  openedAt: string;
  resolvedAt?: string | null;
  closedAt?: string | null;
  summary?: string | null;
  rootCauseDocName?: string | null;
  rootCauseDocPath?: string | null;
  falsePositive: boolean;
  tags: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  // Workflow state fields
  previousStatus?: string | null;
  cooperationEnabled?: boolean;
  statusReason?: string | null;
  // Classification
  primaryCategory?: string | null;
  subcategory?: string | null;
  incidentType?: string | null;
  // Severity matrix
  likelihood?: string | null;
  // Pending details
  pendingRequestedParty?: string | null;
  pendingExpectedResponseDate?: string | null;
  // Reopen details
  reopenReason?: string | null;
  reopenApprovingManager?: string | null;
  reopenReferenceEvidence?: string | null;
  // KPI timestamps
  alertCreatedAt?: string | null;
  firstAcknowledgedAt?: string | null;
  investigationStartedAt?: string | null;
  containedAt?: string | null;
  eradicatedAt?: string | null;
  recoveredAt?: string | null;
  // SLA Deadlines & Breach Tracking (workfinal.txt §1.3)
  ackSlaDue?: string | null;
  ackSlaBreached?: boolean;
  ackSlaBreachedAt?: string | null;
  ackSlaWarningSent?: boolean;
  startSlaDue?: string | null;
  startSlaBreached?: boolean;
  startSlaBreachedAt?: string | null;
  startSlaWarningSent?: boolean;
  containSlaDue?: string | null;
  containSlaBreached?: boolean;
  containSlaBreachedAt?: string | null;
  containSlaWarningSent?: boolean;
  resolveSlaDue?: string | null;
  resolveSlaBreached?: boolean;
  resolveSlaBreachedAt?: string | null;
  resolveSlaWarningSent?: boolean;
  slaEscalationLevel?: number;
  // SLA Pausing Fields
  slaPaused?: boolean;
  slaPausedAt?: string | null;
  totalPausedMinutes?: number;
  // Enriched
  creator?: UserInfo;
  assignee?: UserInfo;
  assignments?: CaseAssignment[];
  comments?: CaseComment[];
  tasks?: CaseTask[];
  timeline?: TimelineEvent[];
  caseAlerts?: CaseAlert[];
  observables?: Observable[];
  evidence?: Evidence[];
  mitreMappings?: CaseMitreMapping[];
  slaPauses?: CaseSlaPause[];
  assignmentHistory?: CaseAssignmentHistory[];
  escalationHistory?: CaseEscalationHistory[];
}

export interface IOC {
  id: string;
  organizationId: string;
  iocType: string;
  iocValue: string;
  threatType: string;
  threatName: string;
  confidence: number;
  tlpRating: string;
  firstSeen: string;
  lastSeen: string;
  expiresAt?: string | null;
  sourceFeed: string;
  tags: string;
  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: Pagination;
}

export interface CaseSummary {
  total: number;
  assignedToMe: number;
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
}

export interface AlertSummary {
  total: number;
  unassigned: number;
  myAcknowledged: number;
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
}

export interface AlertAnalyticsTrends {
  range: 'day' | 'week' | 'month' | 'year' | 'all';
  severityTrend: Array<{
    timestamp: string;
    label: string;
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
    total: number;
  }>;
  outcomesTrend: Array<{
    timestamp: string;
    label: string;
    promoted: number;
    linked: number;
    falsePositive: number;
    inTriage: number;
    total: number;
  }>;
  totals: {
    totalAlerts: number;
    bySeverity: { critical: number; high: number; medium: number; low: number; info: number };
    byOutcome: { promoted: number; linked: number; falsePositive: number; inTriage: number };
    conversionRate: number;
    fpRate: number;
  };
}

export interface CaseAnalyticsTrends {
  range: 'day' | 'week' | 'month' | 'year' | 'all';
  creationOriginTrend: Array<{
    timestamp: string;
    label: string;
    manual: number;
    promoted: number;
    linked: number;
    total: number;
  }>;
  slaComplianceTrend: Array<{
    timestamp: string;
    label: string;
    onTrack: number;
    breached: number;
    total: number;
  }>;
  stageDistribution: Record<string, number>;
  totals: {
    totalCases: number;
    manual: number;
    promoted: number;
    linked: number;
    onTrack: number;
    breached: number;
    slaComplianceRate: number;
  };
}

// ── Query DTOs ────────────────────────────────────────────────────────────────

export interface GetCasesQuery {
  status?: string;
  priority?: number;
  severity?: string;
  assignedToSub?: string;
  organizationId?: string;
  page?: number;
  limit?: number;
  /** tier1 only: 'active' lists every active case in the org (read-only for others' cases). */
  scope?: 'mine' | 'active';
}

export interface GetAlertsQuery {
  status?: string;
  severity?: string;
  sourceId?: string;
  attackType?: string;
  organizationId?: string;
  acknowledgedBySub?: string;
  closedBySub?: string;
  page?: number;
  limit?: number;
}

export interface GetIocsQuery {
  iocType?: string;
  threatType?: string;
  tlpRating?: string;
  activeOnly?: boolean;
  organizationId?: string;
  page?: number;
  limit?: number;
}

// ── Create/Update DTOs ────────────────────────────────────────────────────────

export interface CreateCaseDto {
  title: string;
  description?: string;
  status?: string;
  priority?: number;
  severity?: string;
  caseType?: string;
  assignedTo?: string;
  sourceIp?: string;
  destinationIp?: string;
  httpStatus?: string;
  attackOrigin?: string;
  affectedAsset?: string;
  shift?: string;
  impact?: string;
  recommendation?: string;
  // Classification
  primaryCategory?: string;
  subcategory?: string;
  incidentType?: string;
  likelihood?: string;
  // Alert escalation
  alertId?: string;
}

export interface UpdateCaseDto {
  title?: string;
  description?: string;
  status?: string;
  priority?: number;
  severity?: string;
  assignedTo?: string;
  summary?: string;
  falsePositive?: boolean;
  tags?: string;
  recommendation?: string;
  caseType?: string;
  sourceIp?: string;
  destinationIp?: string;
  httpStatus?: string;
  attackOrigin?: string;
  affectedAsset?: string;
  shift?: string;
  impact?: string;
  // Classification
  primaryCategory?: string;
  subcategory?: string;
  incidentType?: string;
  likelihood?: string;
}

// ── Workflow Action DTOs ──────────────────────────────────────────────────────

export interface EscalateCaseDto {
  reason?: string;
}

export interface TransitionResponseDto {
  status: 'CONTAINMENT' | 'ERADICATION' | 'RECOVERY' | 'RESOLVED';
  summary?: string;
}

export interface UpdateAvailabilityDto {
  onShift?: boolean;
  available?: boolean;
  shiftStart?: string;
  shiftEnd?: string;
}

export interface PendingCaseDto {
  pendingReason: string;
  pendingRequestedParty?: string;
  pendingExpectedResponseDate?: string;
}

export interface ReopenCaseDto {
  reopenReason: string;
  reopenReferenceEvidence?: string;
}

export interface AssignCaseDto {
  /** Legacy single assignee (treated as lead when analystIds omitted) */
  assignedTo?: string;
  /** All assigned tier2/tier3 analysts (must include lead) */
  analystIds?: string[];
  /** Exactly one lead — must be in analystIds */
  leadAnalystId?: string;
  reason?: string;
}

export interface CooperateCaseDto {
  durationHours?: number;
  accessLevel?: 'COMMENT' | 'CONTRIBUTE';
  reason: string;
  requestedBy?: string;
}

/** DTO sent by an investigator when submitting a CERT collaboration request. */
export interface RequestCertCooperationDto {
  reason: string;                                         // min 20 chars
  accessLevel?: 'COMMENT' | 'CONTRIBUTE';  // default: COMMENT
  durationHours?: number;                                 // 1-1440; null → severity default
}

/** DTO sent by a manager when approving or rejecting a request. */
export interface ReviewCertRequestDto {
  action: 'APPROVE' | 'REJECT';
  rejectionReason?: string;   // required when action = REJECT
  accessLevel?: 'COMMENT' | 'CONTRIBUTE'; // manager override
  durationHours?: number;     // manager override
}

/** A CERT cooperation request record — represents one investigator → manager approval cycle. */
export interface CertCooperationRequest {
  id: string;
  caseId: string;
  organizationId: string;
  requestedBy: string;
  requestedByUser?: { displayName?: string; email: string };
  reason: string;
  accessLevel: 'COMMENT' | 'CONTRIBUTE';
  durationHours?: number | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
  accessGrantId?: string | null;
  createdAt: string;
  updatedAt: string;
  // Enriched fields from join (returned by backend)
  caseNumber?: string;
  caseTitle?: string;
  caseSeverity?: string;
}

export interface AddObservableDto {
  type: 'IP' | 'DOMAIN' | 'URL' | 'EMAIL' | 'USERNAME' | 'HOSTNAME' | 'FILE_HASH';
  value: string;
}

export interface AddEvidenceDto {
  fileName: string;
  fileSize?: number;
  mimeType?: string;
  storagePath?: string;
}

export interface AddMitreMappingDto {
  technique: string;
  tactic: string;
}

export interface CreateAlertDto {
  sourceId: string;
  externalAlertId: string;
  alertName: string;
  alertBody: string;
  severity: string;
  attackType: string;
  alertTime: string;
  rawPayload?: string;
  mitreTactic?: string;
  mitreTechnique?: string;
  sourceIp?: string;
  destinationIp?: string;
  attackOrigin?: string;
  assetName?: string;
  username?: string;
  httpStatus?: number;
  enrichment?: string;
}

export interface UpdateAlertDto {
  status?: string;
  enrichment?: string;
  mitreTactic?: string;
  mitreTechnique?: string;
  tags?: string[];
  attachments?: any[];
}

export interface CreateIocDto {
  iocType: string;
  iocValue: string;
  threatType: string;
  threatName: string;
  confidence: number;
  tlpRating: string;
  sourceFeed: string;
  tags?: string;
  expiresAt?: string;
}

export interface AddCommentDto {
  body: string;
  isInternal?: boolean;
  parentCommentId?: string;
}

export interface AddTaskDto {
  title: string;
  description?: string;
  categories?: string[];
  assignedTo?: string;
  dueAt: string;
  priority?: number;
  linkedCaseStatus?: string;
}

export interface UpdateTaskDto {
  title?: string;
  description?: string;
  categories?: string[];
  status?: string;
  assignedTo?: string;
  dueAt?: string;
}

// ── Case Board Types ──────────────────────────────────────────────────────────

export interface GetCaseBoardQuery {
  severity?: string;
  assignedTo?: string;
  createdAfter?: string;
  createdBefore?: string;
  showAllStatuses?: boolean;
  status?: string;
  overdue?: boolean;
}

export interface CaseBoardColumn {
  status: string;
  title?: string;
  description?: string;
  color?: string;
  boardOrder?: number;
  isRemovable?: boolean;
  cases: Case[];
  count: number;
}

export interface IrStageConfig {
  id: string;
  organizationId: string;
  caseStatus: string;
  title: string;
  description: string;
  color: string;
  boardOrder: number;
  isActive: boolean;
  isRemovable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CaseBoardData {
  columns: CaseBoardColumn[];
  total: number;
}

export interface CaseBoardDashboard {
  totalOpen: number;
  critical: number;
  overdue: number;
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
  avgTimePerStage: Record<string, number>;
}

// ── API prefix: /api/case/* → proxied to CMS at localhost:3005/api/* ──────

const CMS = '/case';

// ── Service ───────────────────────────────────────────────────────────────────

export const cmsService = {
  // ── Tenant administration ──────────────────────────────────────────────────

  async listTenantRegistrations(status = 'PENDING'): Promise<Tenant[]> {
    return apiClient.get(`${CMS}/tenancy/registrations?status=${encodeURIComponent(status)}`);
  },

  async listTenants(): Promise<Tenant[]> {
    return apiClient.get(`${CMS}/tenancy/organizations`);
  },

  async approveTenant(id: string): Promise<Tenant> {
    return apiClient.post(`${CMS}/tenancy/registrations/${id}/approve`, {});
  },

  async rejectTenant(id: string, reason?: string): Promise<Tenant> {
    return apiClient.patch(`${CMS}/tenancy/registrations/${id}/reject`, { reason });
  },

  async setTenantStatus(
    id: string,
    status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE',
  ): Promise<Tenant> {
    return apiClient.patch(`${CMS}/tenancy/organizations/${id}/status`, { status });
  },

  // ── IR Stage Config ────────────────────────────────────────────────────────

  async getIrStages(): Promise<IrStageConfig[]> {
    return apiClient.get(`${CMS}/cases/ir-stages`);
  },

  async createIrStage(dto: { caseStatus: string; title: string; description?: string; color?: string }): Promise<IrStageConfig> {
    return apiClient.post(`${CMS}/cases/ir-stages`, dto);
  },

  async updateIrStage(id: string, dto: { title?: string; description?: string; color?: string; isActive?: boolean }): Promise<IrStageConfig> {
    return apiClient.put(`${CMS}/cases/ir-stages/${id}`, dto);
  },

  async deleteIrStage(id: string): Promise<IrStageConfig> {
    return apiClient.delete(`${CMS}/cases/ir-stages/${id}`);
  },

  async reorderIrStages(orderedIds: string[]): Promise<IrStageConfig[]> {
    return apiClient.put(`${CMS}/cases/ir-stages/reorder`, { orderedIds });
  },

  // ── Cases ─────────────────────────────────────────────────────────────────


  async listCases(query?: GetCasesQuery): Promise<PaginatedResponse<Case>> {
    const params: Record<string, any> = { ...query };
    const validStatuses = [
      'DRAFT', 'OPEN_PRIVATE', 'ESCALATED', 'SOC_LEADER_REVIEW', 'ASSIGNED',
      'IN_INVESTIGATION', 'CONTAINMENT', 'ERADICATION', 'RECOVERY',
      'PENDING', 'RESOLVED', 'CLOSED', 'REOPENED',
    ];
    if (params.status && !validStatuses.includes(params.status)) {
      delete params.status;
    }
    return apiClient.get(`${CMS}/cases`, { params });
  },

  async getCaseSummary(): Promise<CaseSummary> {
    return apiClient.get(`${CMS}/cases/summary`);
  },

  async getEscalationKpis(): Promise<EscalationKpis> {
    return apiClient.get(`${CMS}/cases/escalation-kpis`);
  },

  async getAssignedCases(): Promise<{ data: Case[]; total: number }> {
    return apiClient.get(`${CMS}/cases/assigned`);
  },

  async getActiveInvestigations(): Promise<{ data: Case[]; total: number }> {
    return apiClient.get(`${CMS}/cases/active-investigations`);
  },

  async getCase(id: string): Promise<Case> {
    return apiClient.get(`${CMS}/cases/${id}`);
  },

  async createCase(dto: CreateCaseDto): Promise<Case> {
    return apiClient.post(`${CMS}/cases`, dto);
  },

  async downloadRootCauseDocument(caseId: string): Promise<Blob> {
    const res: any = await apiClient.get(`${CMS}/cases/${caseId}/root-cause-document`, {
      responseType: 'blob',
    });
    return res.data ?? res;
  },

  async uploadRootCauseDocument(caseId: string, file: File): Promise<Case> {
    const formData = new FormData();
    formData.append('file', file);
    const res: any = await apiClient.post(`${CMS}/cases/${caseId}/root-cause-document`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data ?? res;
  },

  async updateCase(id: string, dto: UpdateCaseDto): Promise<Case> {
    return apiClient.patch(`${CMS}/cases/${id}`, dto);
  },

  async deleteCase(id: string): Promise<{ id: string }> {
    return apiClient.delete(`${CMS}/cases/${id}`);
  },

  // ── Workflow Transitions ──────────────────────────────────────────────────

  async escalateCase(id: string, dto?: EscalateCaseDto): Promise<Case> {
    return apiClient.post(`${CMS}/cases/${id}/escalate`, dto || {});
  },

  async reviewCase(id: string): Promise<Case> {
    return apiClient.post(`${CMS}/cases/${id}/review`);
  },

  async assignCase(id: string, dto: AssignCaseDto): Promise<Case> {
    return apiClient.post(`${CMS}/cases/${id}/assign`, dto);
  },

  async startInvestigation(id: string): Promise<Case> {
    return apiClient.post(`${CMS}/cases/${id}/start-investigation`);
  },

  async transitionResponse(id: string, dto: TransitionResponseDto): Promise<Case> {
    return apiClient.post(`${CMS}/cases/${id}/transition-response`, dto);
  },

  async pendingCase(id: string, dto: PendingCaseDto): Promise<Case> {
    return apiClient.post(`${CMS}/cases/${id}/pending`, dto);
  },

  async resumeCase(id: string): Promise<Case> {
    return apiClient.post(`${CMS}/cases/${id}/resume`);
  },

  async reopenCase(id: string, dto: ReopenCaseDto): Promise<Case> {
    return apiClient.post(`${CMS}/cases/${id}/reopen`, dto);
  },

  // ── Observables ───────────────────────────────────────────────────────────

  async listObservables(caseId: string): Promise<Observable[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/observables`);
  },

  async addObservable(caseId: string, dto: AddObservableDto): Promise<Observable> {
    return apiClient.post(`${CMS}/cases/${caseId}/observables`, dto);
  },

  async removeObservable(caseId: string, observableId: string): Promise<{ id: string }> {
    return apiClient.delete(`${CMS}/cases/${caseId}/observables/${observableId}`);
  },

  // ── Attachments ────────────────────────────────────────────────────────────

  async listAttachments(caseId: string): Promise<Evidence[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/attachments`);
  },

  async uploadAttachment(caseId: string, file: File): Promise<Evidence> {
    const formData = new FormData();
    formData.append('file', file);
    const res: any = await apiClient.post(`${CMS}/cases/${caseId}/attachments/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data ?? res;
  },

  async listEvidence(caseId: string): Promise<Evidence[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/evidence`);
  },

  async addEvidence(caseId: string, dto: AddEvidenceDto): Promise<Evidence> {
    return apiClient.post(`${CMS}/cases/${caseId}/evidence`, dto);
  },

  async removeEvidence(caseId: string, evidenceId: string): Promise<{ id: string }> {
    return apiClient.delete(`${CMS}/cases/${caseId}/evidence/${evidenceId}`);
  },

  async downloadAttachment(caseId: string, evidenceId: string, fileName?: string): Promise<void> {
    try {
      const res: any = await apiClient.get(`${CMS}/cases/${caseId}/attachments/${evidenceId}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data ?? res]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `attachment-${evidenceId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      const fallbackBlob = new Blob([`Attachment: ${fileName || evidenceId}\nCase: ${caseId}`], {
        type: 'text/plain;charset=utf-8',
      });
      const url = window.URL.createObjectURL(fallbackBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `attachment-${evidenceId}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  },

  // ── MITRE ATT&CK Mappings ────────────────────────────────────────────────

  async getMitreCatalog(): Promise<any[]> {
    return apiClient.get(`${CMS}/cases/mitre/catalog`);
  },

  async upsertMitreCatalogItem(dto: any): Promise<any> {
    return apiClient.post(`${CMS}/cases/mitre/catalog`, dto);
  },

  async listMitreMappings(caseId: string): Promise<CaseMitreMapping[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/mitre`);
  },

  async addMitreMapping(caseId: string, dto: AddMitreMappingDto): Promise<CaseMitreMapping> {
    return apiClient.post(`${CMS}/cases/${caseId}/mitre`, dto);
  },

  async removeMitreMapping(caseId: string, technique: string): Promise<{ caseId: string; technique: string }> {
    return apiClient.delete(`${CMS}/cases/${caseId}/mitre/${technique}`);
  },

  async updateMitreMapping(caseId: string, technique: string, dto: Partial<AddMitreMappingDto>): Promise<CaseMitreMapping> {
    return apiClient.put(`${CMS}/cases/${caseId}/mitre/${technique}`, dto);
  },

  // ── Assignment Recommendations ────────────────────────────────────────────

  async getAssignmentRecommendations(caseId: string): Promise<AssignmentRecommendation[]> {
    const raw = await apiClient.get<any[]>(`${CMS}/cases/${caseId}/assignment-recommendations`);
    return (Array.isArray(raw) ? raw : []).map((item) => this.normalizeInvestigator(item));
  },

  async getAssignableInvestigators(caseId: string): Promise<AssignmentRecommendation[]> {
    const raw = await apiClient.get<any[]>(`${CMS}/cases/${caseId}/assignable-investigators`);
    return (Array.isArray(raw) ? raw : []).map((item) => this.normalizeInvestigator(item));
  },

  normalizeInvestigator(item: Record<string, unknown>): AssignmentRecommendation {
    return {
      sub: String(item.sub ?? item.keycloakSub ?? item.id ?? ''),
      email: String(item.email ?? ''),
      displayName: (item.displayName ?? item.fullName) as string | undefined,
      username: item.username as string | undefined,
      role: String(item.role ?? (item.roles as string[] | undefined)?.[0] ?? ''),
      workloadScore: Number(item.workloadScore ?? 0),
      activeCases: Number(item.activeCases ?? item.activeCasesCount ?? 0),
      isRecommended: Boolean(item.isRecommended),
      onShift: item.onShift as boolean | undefined,
      available: item.available as boolean | undefined,
    };
  },

  // ── CERT Cooperation (direct grant — manager) ─────────────────────────────

  async grantCooperation(caseId: string, dto: CooperateCaseDto): Promise<{ token: string; expiresAt: string }> {
    return apiClient.post(`${CMS}/cases/${caseId}/cooperate`, dto);
  },

  async revokeCooperation(caseId: string, reason?: string): Promise<{ message: string }> {
    return apiClient.delete(`${CMS}/cases/${caseId}/cooperate`, { data: { reason } });
  },

  async revokeCooperationAccess(caseId: string, reason?: string): Promise<{ message: string }> {
    return this.revokeCooperation(caseId, reason);
  },

  async listCooperation(caseId: string): Promise<CertCooperationAccess[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/cooperate`);
  },

  async getCertReport(caseId: string): Promise<CertIncidentReport> {
    return apiClient.get(`${CMS}/cases/${caseId}/cert-report`);
  },

  async retryCertReport(caseId: string): Promise<CertIncidentReport> {
    return apiClient.post(`${CMS}/cases/${caseId}/cert-report/retry`);
  },

  // ── CERT Cooperation Request Workflow (investigator → manager pipeline) ───

  /** Investigator submits a formal CERT collaboration request. */
  async submitCooperationRequest(
    caseId: string,
    dto: RequestCertCooperationDto,
  ): Promise<CertCooperationRequest> {
    return apiClient.post(`${CMS}/cases/${caseId}/cooperate/request`, dto);
  },

  /** List CERT requests for a case. Role-filtered by backend (investigators see own, managers see all). */
  async listCooperationRequests(caseId: string): Promise<CertCooperationRequest[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/cooperate/requests`);
  },

  /**
   * Manager approves or rejects a pending request.
   * On APPROVE: response includes { token, expiresAt, accessLevel } — show token ONCE in non-dismissible modal.
   */
  async reviewCooperationRequest(
    caseId: string,
    reqId: string,
    dto: ReviewCertRequestDto,
  ): Promise<{ requestId: string; status: string; token?: string; expiresAt?: string; accessLevel?: string; caseNumber?: string; caseTitle?: string; rejectionReason?: string }> {
    return apiClient.post(`${CMS}/cases/${caseId}/cooperate/requests/${reqId}/review`, dto);
  },

  /** Investigator cancels their own pending request. */
  async cancelCooperationRequest(caseId: string, reqId: string): Promise<{ requestId: string; status: string }> {
    return apiClient.delete(`${CMS}/cases/${caseId}/cooperate/requests/${reqId}`);
  },

  /** Manager approval queue — all PENDING requests across the org. */
  async listPendingCooperationRequests(): Promise<CertCooperationRequest[]> {
    return apiClient.get(`${CMS}/cases/cooperate/requests/pending`);
  },

  /** List ALL historical CERT cooperation requests across the org. */
  async listAllCooperationRequests(): Promise<CertCooperationRequest[]> {
    return apiClient.get(`${CMS}/cases/cooperate/requests/all`);
  },

  /** List ALL active CERT cooperation grants across the org. */
  async listActiveCooperationGrants(): Promise<any[]> {
    return apiClient.get(`${CMS}/cases/cooperate/active`);
  },

  /** Get CERT collaboration metrics summary for the org dashboard. */
  async getCertCooperationMetrics(): Promise<{ pending: number; active: number; approved: number; rejected: number }> {
    return apiClient.get(`${CMS}/cases/cooperate/metrics`);
  },

  // ── Threat Intelligence Integrations ──────────────────────────────────────

  async getThreatIntelApps(): Promise<Array<{
    id: string;
    name: string;
    provider: string;
    apiUrl?: string | null;
    description: string;
    logoUrl?: string | null;
    isBuiltin: boolean;
    createdBy: string;
    createdAt: string;
    hasUserKey: boolean;
    userApiKeyMasked?: string | null;
  }>> {
    return apiClient.get(`${CMS}/cases/threat-intel/apps`);
  },

  async registerThreatIntelApp(dto: { appId?: string; name: string; provider?: string; apiUrl?: string; description?: string; logoUrl?: string }): Promise<any> {
    return apiClient.post(`${CMS}/cases/threat-intel/apps`, dto);
  },

  async deleteThreatIntelApp(appId: string): Promise<any> {
    return apiClient.delete(`${CMS}/cases/threat-intel/apps/${appId}`);
  },

  async saveUserThreatIntelCredential(appId: string, apiKey: string): Promise<any> {
    return apiClient.post(`${CMS}/cases/threat-intel/credentials`, { appId, apiKey });
  },

  async enrichObservable(caseId: string, observableId: string, provider: string): Promise<Observable> {
    return apiClient.post(`${CMS}/cases/threat-intel/enrich/${caseId}/${observableId}`, { provider });
  },

  async enrichAlertObservable(alertId: string, observableId: string, provider: string): Promise<any> {
    return apiClient.post(`${CMS}/cases/threat-intel/enrich-alert/${alertId}/${observableId}`, { provider });
  },

  // ── Manager Configurable SLA Clocks ──────────────────────────────────────

  async getSlaConfig(): Promise<Array<{
    id?: string;
    organizationId?: string;
    slaType: 'ACK' | 'START' | 'CONTAIN' | 'RESOLVE';
    severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
    durationMins: number;
    updatedBySub?: string | null;
    updatedByEmail?: string | null;
    updatedAt?: string;
  }>> {
    return apiClient.get(`${CMS}/cases/sla/config`);
  },

  async updateSlaConfig(configs: Array<{
    slaType: 'ACK' | 'START' | 'CONTAIN' | 'RESOLVE';
    severity: string;
    durationMins: number;
  }>): Promise<any> {
    return apiClient.post(`${CMS}/cases/sla/config`, { configs });
  },

  async resetSlaConfig(): Promise<any> {
    return apiClient.post(`${CMS}/cases/sla/config/reset`);
  },

  async getUnescalatedPolicyConfig(): Promise<{ durationMins: number; notifyDaily: boolean; notifyManagers: boolean; notifyCreator: boolean }> {
    return apiClient.get(`${CMS}/cases/sla/config/unescalated-policy`);
  },

  async updateUnescalatedPolicyConfig(durationMins: number): Promise<any> {
    return apiClient.put(`${CMS}/cases/sla/config/unescalated-policy`, { durationMins });
  },

  async revertCaseToAlert(caseId: string): Promise<any> {
    return apiClient.post(`${CMS}/cases/${caseId}/revert-to-alert`);
  },

  async deleteManualCase(caseId: string): Promise<any> {
    return apiClient.delete(`${CMS}/cases/${caseId}/manual`);
  },

  async getUnescalatedCases(): Promise<{ policyDurationMins: number; totalUnescalated: number; cases: any[] }> {
    return apiClient.get(`${CMS}/cases/unescalated`);
  },

  // ── Comments ──────────────────────────────────────────────────────────────

  async addComment(caseId: string, dto: AddCommentDto): Promise<CaseComment> {
    return apiClient.post(`${CMS}/cases/${caseId}/comments`, dto);
  },

  async listComments(caseId: string): Promise<CaseComment[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/comments`);
  },

  async deleteComment(caseId: string, commentId: string): Promise<{ id: string }> {
    return apiClient.delete(`${CMS}/cases/${caseId}/comments/${commentId}`);
  },

  // ── Tasks ─────────────────────────────────────────────────────────────────

  async addTask(caseId: string, dto: AddTaskDto): Promise<CaseTask> {
    return apiClient.post(`${CMS}/cases/${caseId}/tasks`, dto);
  },

  async listTasks(caseId: string): Promise<CaseTask[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/tasks`);
  },

  async getTaskBoard(caseId: string): Promise<CaseTask[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/task-board`);
  },

  async updateTask(caseId: string, taskId: string, dto: UpdateTaskDto): Promise<CaseTask> {
    return apiClient.put(`${CMS}/cases/${caseId}/tasks/${taskId}`, dto);
  },

  async deleteTask(caseId: string, taskId: string): Promise<{ id: string }> {
    return apiClient.delete(`${CMS}/cases/${caseId}/tasks/${taskId}`);
  },

  // ── Task Items (Subtasks) ──────────────────────────────────────────────────

  async addTaskItem(caseId: string, taskId: string, dto: { title: string }): Promise<CaseTaskItem> {
    return apiClient.post(`${CMS}/cases/${caseId}/tasks/${taskId}/items`, dto);
  },

  async updateTaskItem(caseId: string, taskId: string, itemId: string, dto: { title?: string; isChecked?: boolean }): Promise<CaseTaskItem> {
    return apiClient.put(`${CMS}/cases/${caseId}/tasks/${taskId}/items/${itemId}`, dto);
  },

  async deleteTaskItem(caseId: string, taskId: string, itemId: string): Promise<{ id: string }> {
    return apiClient.delete(`${CMS}/cases/${caseId}/tasks/${taskId}/items/${itemId}`);
  },

  // ── Task Templates ─────────────────────────────────────────────────────────

  async getTaskTemplates(): Promise<TaskTemplate[]> {
    return apiClient.get(`${CMS}/cases/task-templates`);
  },

  async createTaskTemplate(dto: any): Promise<TaskTemplate> {
    return apiClient.post(`${CMS}/cases/task-templates`, dto);
  },

  async deleteTaskTemplate(templateId: string): Promise<{ id: string }> {
    return apiClient.delete(`${CMS}/cases/task-templates/${templateId}`);
  },

  async applyTaskTemplate(caseId: string, templateId: string): Promise<{ applied: number; skipped: number }> {
    return apiClient.post(`${CMS}/cases/${caseId}/apply-template`, { templateId });
  },



  // ── Timeline ──────────────────────────────────────────────────────────────

  async getTimeline(caseId: string): Promise<TimelineEvent[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/timeline`);
  },

  // ── Alert Linking ─────────────────────────────────────────────────────────

  async linkAlert(caseId: string, dto: LinkCaseAlertDto): Promise<CaseAlert> {
    return apiClient.post(`${CMS}/cases/${caseId}/alerts`, dto);
  },

  async listLinkedAlerts(caseId: string): Promise<CaseAlert[]> {
    return apiClient.get(`${CMS}/cases/${caseId}/alerts`);
  },

  async unlinkAlert(caseId: string, alertId: string): Promise<{ caseId: string; alertId: string }> {
    return apiClient.delete(`${CMS}/cases/${caseId}/alerts/${alertId}`);
  },

  // ── Alerts ────────────────────────────────────────────────────────────────

  async listAlerts(query?: GetAlertsQuery): Promise<PaginatedResponse<Alert>> {
    return apiClient.get(`${CMS}/alerts`, { params: query });
  },

  async getAlertSummary(): Promise<AlertSummary> {
    return apiClient.get(`${CMS}/alerts/summary`);
  },

  async getAlertTrends(range: 'day' | 'week' | 'month' | 'year' | 'all' = 'week'): Promise<AlertAnalyticsTrends> {
    return apiClient.get(`${CMS}/alerts/analytics/trends`, { params: { range } });
  },

  async getCaseTrends(range: 'day' | 'week' | 'month' | 'year' | 'all' = 'week'): Promise<CaseAnalyticsTrends> {
    return apiClient.get(`${CMS}/cases/analytics/trends`, { params: { range } });
  },

  async getAlert(id: string): Promise<Alert> {
    return apiClient.get(`${CMS}/alerts/${id}`);
  },

  async createAlert(dto: CreateAlertDto): Promise<Alert> {
    return apiClient.post(`${CMS}/alerts`, dto);
  },

  async updateAlert(id: string, dto: UpdateAlertDto): Promise<Alert> {
    return apiClient.patch(`${CMS}/alerts/${id}`, dto);
  },

  async acknowledgeAlert(id: string): Promise<Alert> {
    return apiClient.patch(`${CMS}/alerts/${id}/acknowledge`);
  },


  async closeAlert(id: string, dto?: { falsePositive?: boolean; reason?: string }): Promise<Alert> {
    return apiClient.patch(`${CMS}/alerts/${id}/close`, dto ?? {});
  },

  async getSimilarCases(id: string): Promise<SimilarCase[]> {
    return apiClient.get(`${CMS}/alerts/${id}/similar-cases`);
  },

  async getSimilarAlerts(id: string): Promise<SimilarAlert[]> {
    return apiClient.get(`${CMS}/alerts/${id}/similar-alerts`);
  },

  async getCaseSimilarCases(id: string): Promise<SimilarCase[]> {
    return apiClient.get(`${CMS}/cases/${id}/similar-cases`);
  },

  async getCaseSimilarAlerts(id: string): Promise<SimilarAlert[]> {
    return apiClient.get(`${CMS}/cases/${id}/similar-alerts`);
  },

  // getAlertHistory retired — alert history now comes straight from
  // audit-service via @/services/auditService's entityHistory().

  async addAlertObservable(id: string, dto: { type: string; value: string }): Promise<AlertObservable> {
    return apiClient.post(`${CMS}/alerts/${id}/observables`, dto);
  },

  async deleteAlertObservable(id: string, observableId: string): Promise<{ success: boolean }> {
    return apiClient.delete(`${CMS}/alerts/${id}/observables/${observableId}`);
  },

  async escalateAlert(id: string, dto?: EscalateAlertDto): Promise<Case> {
    return apiClient.post(`${CMS}/alerts/${id}/escalate`, dto ?? {});
  },

  // ── Alert Sources ─────────────────────────────────────────────────────────

  async listAlertSources(): Promise<AlertSource[]> {
    return apiClient.get(`${CMS}/alert-sources`);
  },

  async getAlertSource(id: string): Promise<AlertSource> {
    return apiClient.get(`${CMS}/alert-sources/${id}`);
  },

  async createAlertSource(dto: CreateAlertSourceDto): Promise<AlertSource> {
    return apiClient.post(`${CMS}/alert-sources`, dto);
  },

  async updateAlertSource(id: string, dto: UpdateAlertSourceDto): Promise<AlertSource> {
    return apiClient.put(`${CMS}/alert-sources/${id}`, dto);
  },

  async pauseAlertSource(id: string): Promise<AlertSource> {
    return apiClient.patch(`${CMS}/alert-sources/${id}/pause`);
  },

  async resumeAlertSource(id: string): Promise<AlertSource> {
    return apiClient.patch(`${CMS}/alert-sources/${id}/resume`);
  },

  async deactivateAlertSource(id: string): Promise<{ id: string; isActive: boolean; status: string }> {
    return apiClient.delete(`${CMS}/alert-sources/${id}`);
  },

  async disconnectAlertSource(id: string): Promise<{ id: string; isActive: boolean; status: string }> {
    return apiClient.delete(`${CMS}/alert-sources/${id}`);
  },

  // ── Case Board (Kanban View) ──────────────────────────────────────────────

  async getCaseBoard(filters?: GetCaseBoardQuery): Promise<CaseBoardData> {
    return apiClient.get(`${CMS}/cases/board`, { params: filters });
  },

  async getCaseBoardDashboard(): Promise<CaseBoardDashboard> {
    return apiClient.get(`${CMS}/cases/board/dashboard`);
  },

  // ── CMS Users (Team Management) ──────────────────────────────────────────

  async listOrgUsers(): Promise<CmsOrgUser[]> {
    return apiClient.get(`${CMS}/cms-users`);
  },

  // Standalone/on-prem only (see IS_CMS_BUNDLE in CmsTeamPage.tsx) — the
  // embedded platform's main Team Management page covers this via
  // POST /api/users/org-members/invite directly.
  async inviteUser(dto: InviteUserDto): Promise<InviteUserResponse> {
    return apiClient.post(`${CMS}/cms-users`, dto);
  },

  async changeUserRole(userId: string, dto: ChangeRoleDto): Promise<any> {
    return apiClient.patch(`${CMS}/cms-users/${userId}/role`, dto);
  },

  async removeUser(userId: string): Promise<{ message: string }> {
    return apiClient.delete(`${CMS}/cms-users/${userId}`);
  },

  async listRoles(): Promise<{ roles: CmsRole[] }> {
    return apiClient.get(`${CMS}/cms-users/roles`);
  },

  async getRolePermissions(roleName: string): Promise<{ role: string; permissions: string[] }> {
    return apiClient.get(`${CMS}/cms-users/roles/${roleName}/permissions`);
  },

  async getPermissionsMatrix(): Promise<{ matrix: Record<string, CmsRoleWithPermissions> }> {
    return apiClient.get(`${CMS}/cms-users/permissions-matrix`);
  },

  async getAnalystAvailability(userId: string): Promise<AnalystAvailability> {
    return apiClient.get(`${CMS}/cms-users/${userId}/availability`);
  },

  async updateAnalystAvailability(userId: string, dto: UpdateAvailabilityDto): Promise<AnalystAvailability> {
    return apiClient.patch(`${CMS}/cms-users/${userId}/availability`, dto);
  },

  // Notifications: moved off this service — case-management-service now
  // publishes notify.* events and notification-service owns the REST API.
  // See @/services/notificationsService.

  // Audit: moved off this service — case-management-service now publishes
  // audit events and audit-service owns the REST API. See
  // @/services/auditService.

  // ── Plans ─────────────────────────────────────────────────────────────────

  async listPlans(): Promise<{ scope: string; plans: PlanDefinition[] }> {
    return apiClient.get(`${CMS}/plans`);
  },

  async getCurrentPlan(): Promise<CurrentPlanResponse> {
    return apiClient.get(`${CMS}/plans/current`);
  },

  async activateTrial(): Promise<void> {
    return apiClient.post(`${CMS}/plans/activate-trial`, {});
  },

  // ── Plan Pricing (Platform Admin) ─────────────────────────────────────────

  async getPricingConfig(): Promise<PlanPricingConfig> {
    return apiClient.get(`${CMS}/plans/pricing`);
  },

  async getPlanPricingSchema(planName: string): Promise<PlanPricingSchema> {
    return apiClient.get(`${CMS}/plans/pricing/schema/${planName}`);
  },

  async updateExchangeRate(dto: { usdToEtbRate: number }): Promise<{ usdToEtbRate: number; message?: string }> {
    return apiClient.patch(`${CMS}/plans/pricing/exchange-rate`, dto);
  },

  async updatePlanPricing(planName: string, dto: Partial<PlanPriceUsd>): Promise<{ message?: string }> {
    return apiClient.patch(`${CMS}/plans/pricing/${planName}`, dto);
  },

  async calculatePricing(dto: CalculatePricingDto): Promise<PricingBreakdown> {
    return apiClient.post(`${CMS}/plans/calculate`, dto);
  },

  async requestUpgrade(dto: RequestUpgradeDto): Promise<UpgradeRequestResponse> {
    const res: UpgradeRequestResponse = await apiClient.post(`${CMS}/plans/request`, dto);
    try {
      const localReqs = JSON.parse(localStorage.getItem('kibo_submitted_upgrades') || '[]');
      const newReq: SubscriptionRequest = {
        id: res.requestId || `local-${Date.now()}`,
        organizationId: 'current-org',
        planName: dto.planName,
        users: dto.users,
        casesPerMonth: dto.casesPerMonth,
        addIntegration: !!dto.addIntegration,
        addSoar: !!dto.addSoar,
        contractYears: dto.contractYears,
        currency: dto.currency || 'ETB',
        totalContractValue: res.calculation.pricing.totalContractValue,
        notes: dto.notes,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        organization: { id: 'current-org', name: 'My Organization', slug: 'my-org' }
      };
      localReqs.unshift(newReq);
      localStorage.setItem('kibo_submitted_upgrades', JSON.stringify(localReqs));
    } catch (e) {
      console.error('Failed to store local upgrade request', e);
    }
    return res;
  },


  async listSubscriptionRequests(): Promise<SubscriptionRequest[]> {
    try {
      const res: any = await apiClient.get(`${CMS}/plans/requests`);
      return Array.isArray(res) ? res : res?.requests || res?.data || [];
    } catch (err: any) {
      if (err?.response?.status === 403) {
        const localReqs = JSON.parse(localStorage.getItem('kibo_submitted_upgrades') || '[]');
        try {
          const currentPlan = await cmsService.getCurrentPlan();
          if (currentPlan && currentPlan.requestHistory) {
            let changed = false;
            const syncedReqs = localReqs.map((req: any) => {
              // Match by server-assigned ID first
              let serverReq = currentPlan.requestHistory?.find((sr: any) => sr.id === req.id);
              // Fallback: match by planName for older requests with local- IDs
              if (!serverReq && req.id.startsWith('local-')) {
                serverReq = currentPlan.requestHistory?.find((sr: any) => sr.planName === req.planName);
              }

              if (serverReq && (req.status !== serverReq.status || req.rejectionReason !== serverReq.rejectionReason)) {
                changed = true;
                return {
                  ...req,
                  id: serverReq.id, // Update to official server-assigned ID
                  status: serverReq.status,
                  rejectionReason: serverReq.rejectionReason || null,
                  updatedAt: serverReq.updatedAt,
                };
              }
              return req;
            });
            if (changed) {
              localStorage.setItem('kibo_submitted_upgrades', JSON.stringify(syncedReqs));
              return syncedReqs;
            }
          }
        } catch (syncErr) {
          console.error('Failed to sync local upgrade requests with server', syncErr);
        }
        return localReqs;
      }
      throw err;
    }
  },


  async listSubscribedOrganizations(): Promise<SubscribedOrganization[]> {
    return apiClient.get(`${CMS}/plans/subscriptions`);
  },

  async confirmUpgradeRequest(requestId: string): Promise<{ message: string }> {
    return apiClient.post(`${CMS}/plans/confirm`, { requestId });
  },

  async withdrawUpgradeRequest(requestId: string): Promise<{ message: string }> {
    // Handle local requests (stored in localStorage)
    if (requestId.startsWith('local-')) {
      const localReqs = JSON.parse(localStorage.getItem('kibo_submitted_upgrades') || '[]');
      const filtered = localReqs.filter((r: any) => r.id !== requestId);
      localStorage.setItem('kibo_submitted_upgrades', JSON.stringify(filtered));
      return { message: 'Upgrade request withdrawn successfully' };
    }
    // For server-stored requests, call the API
    return apiClient.post(`${CMS}/plans/withdraw`, { requestId });
  },

  async rejectUpgradeRequest(requestId: string, reason: string): Promise<{ message: string }> {
    // Handle local requests (stored in localStorage)
    if (requestId.startsWith('local-')) {
      const localReqs = JSON.parse(localStorage.getItem('kibo_submitted_upgrades') || '[]');
      const updatedReqs = localReqs.map((r: any) =>
        r.id === requestId ? { ...r, status: 'rejected', rejectionReason: reason, updatedAt: new Date().toISOString() } : r
      );
      localStorage.setItem('kibo_submitted_upgrades', JSON.stringify(updatedReqs));
      return { message: 'Upgrade request rejected successfully' };
    }
    // For server-stored requests, call the API
    return apiClient.post(`${CMS}/plans/reject`, { requestId, reason });
  },

  // ── On-premise licensing ──────────────────────────────────────────────────

  /** On-premise only: import a license file issued by Kibo. */
  async importLicense(license: string): Promise<LicenseStatusResponse> {
    return apiClient.post(`${CMS}/license`, { license });
  },

  /** Platform and SaaS: "On-premise — contact us" from the Plan page. */
  async submitOnPremInquiry(dto: OnPremInquiryDto): Promise<{ message: string }> {
    return apiClient.post(`${CMS}/plans/onprem-inquiry`, dto);
  },

  // ── Customers & licenses (platform_admin) ─────────────────────────────────

  async getVendorSummary(): Promise<VendorSummary> {
    return apiClient.get(`${CMS}/vendor/summary`);
  },

  async listCustomers(): Promise<VendorCustomer[]> {
    return apiClient.get(`${CMS}/vendor/customers`);
  },

  async createCustomer(dto: CustomerInput): Promise<VendorCustomer> {
    return apiClient.post(`${CMS}/vendor/customers`, dto);
  },

  async updateCustomer(id: string, dto: Partial<CustomerInput> & { stage?: 'LEAD' | 'LOST' }): Promise<VendorCustomer> {
    return apiClient.patch(`${CMS}/vendor/customers/${id}`, dto);
  },

  async decodeLicenseRequest(requestFile: string): Promise<LicenseRequestInfo> {
    return apiClient.post(`${CMS}/vendor/license-requests/decode`, { requestFile });
  },

  async issueLicense(customerId: string, dto: IssueLicenseInput): Promise<LicenseFileResponse> {
    return apiClient.post(`${CMS}/vendor/customers/${customerId}/licenses`, dto);
  },

  async getLicenseFile(licenseId: string): Promise<LicenseFileResponse> {
    return apiClient.get(`${CMS}/vendor/licenses/${licenseId}/file`);
  },
};

// ── On-premise licensing types ───────────────────────────────────────────────

export type DeploymentKind = 'platform' | 'onprem' | 'saas';

export interface InstalledLicense {
  id: string;
  customer: string;
  plan: string;
  type: 'PILOT' | 'PAID';
  maxUsers: number;
  maxCasesPerMonth: number;
  integration: boolean;
  soar: boolean;
  validFrom: string;
  expiresAt: string;
  graceDays: number;
  graceEndsAt: string;
  importedAt: string;
  daysLeft: number;
  state: 'active' | 'expiring' | 'grace' | 'expired';
}

export interface LicenseStatusResponse {
  installationId: string;
  license: InstalledLicense | null;
  history: Array<Pick<InstalledLicense, 'id' | 'customer' | 'plan' | 'type' | 'validFrom' | 'expiresAt' | 'importedAt'> & { status: string }>;
}

export interface OnPremInquiryDto {
  contactName: string;
  contactEmail: string;
  phone?: string;
  expectedUsers?: number;
  message?: string;
}

export type CustomerStatus = 'LEAD' | 'PILOT' | 'ACTIVE' | 'EXPIRING' | 'EXPIRED' | 'LOST';

export interface IssuedLicenseSummary {
  id: string;
  installationId: string;
  plan: string;
  type: 'PILOT' | 'PAID';
  maxUsers: number;
  maxCasesPerMonth: number;
  integration: boolean;
  soar: boolean;
  validFrom: string;
  expiresAt: string;
  graceDays: number;
  notes?: string | null;
  createdAt: string;
}

export interface CustomerInput {
  name: string;
  contactName?: string;
  contactEmail?: string;
  phone?: string;
  sector?: string;
  expectedUsers?: number;
  notes?: string;
}

export interface VendorCustomer extends CustomerInput {
  id: string;
  stage: 'LEAD' | 'LOST';
  source: 'manual' | 'contact_form';
  status: CustomerStatus;
  daysLeft: number | null;
  currentLicense: IssuedLicenseSummary | null;
  licenses: IssuedLicenseSummary[];
  createdAt: string;
  updatedAt: string;
}

export interface VendorSummary {
  onPrem: { customers: number; renewalsDue: number; pilots: number; expired: number };
  pipeline: { leads: number; pilots: number; lost: number };
  hosted: { organizations: number | null; paidOrganizations: number | null };
  renewals: Array<{ id: string; name: string; status: CustomerStatus; daysLeft: number | null; plan?: string }>;
  signingConfigured: boolean;
}

export interface LicenseRequestInfo {
  installationId: string;
  organization: string;
  requestedBy: string | null;
  planName: string;
  users: number;
  casesPerMonth: number;
  addIntegration: boolean;
  addSoar: boolean;
  contractYears: number;
  currency: string;
  totalContractValue: number;
  createdAt: string;
}

export interface IssueLicenseInput {
  installationId: string;
  plan: string;
  type: 'PILOT' | 'PAID';
  maxUsers: number;
  maxCasesPerMonth: number;
  integration?: boolean;
  soar?: boolean;
  validFrom?: string;
  durationDays: number;
  graceDays?: number;
  notes?: string;
}

export interface LicenseFileResponse {
  licenseId: string;
  fileName: string;
  file: string;
}

/** Save text as a file in the browser. */
export function downloadTextFile(fileName: string, content: string, mime = 'application/json') {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── AlertSource Types ────────────────────────────────────────────────────────

export type AlertSourceOperationalStatus = 'CONNECTED' | 'Active' | 'PAUSED' | 'Inactive';

export interface AlertSource {
  id: string;
  organizationId: string;
  sourceName: string;
  sourceType: string;
  vendor: string;
  integrationMethod: string;
  endpointUrl?: string | null;
  status: AlertSourceOperationalStatus;
  lastAlertAt?: string | null;
  lastSyncedAt?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  hasApiKey: boolean;
  integration?: {
    method: string;
    apiKey?: string;
    apiEndpointUrl?: string;
    webhookUrl?: string;
    selectedEndpointUrl?: string;
    headers: Record<string, string>;
  };
}

export interface CreateAlertSourceDto {
  sourceName: string;
  sourceType: string;
  vendor: string;
  integrationMethod: string;
  organizationId?: string;
}

export interface UpdateAlertSourceDto {
  sourceName?: string;
  sourceType?: string;
  vendor?: string;
  integrationMethod?: string;
  endpointUrl?: string;
  apiKey?: string;
}

// ── CMS Users (Team Management) Types ────────────────────────────────────────

export interface CmsOrgUser {
  id: string;
  keycloakSub?: string;
  email: string;
  username?: string;
  role: string;
  organizationId: string | null;
  isActive: boolean;
  fullName?: string;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface CmsRole {
  name: string;
  displayName: string;
  description: string;
  level: number;
  color: string;
}

export interface CmsRoleWithPermissions extends CmsRole {
  permissions: string[];
}

export interface InviteUserDto {
  email: string;
  role: string;
  displayName?: string;
  defaultPassword?: string;
}

export interface InviteUserResponse {
  id: string;
  email: string;
  role: string;
  organizationId: string;
  status: 'active' | 'pending';
  inviteMode?: 'existing_user' | 'new_user';
  activationMode?: 'direct' | 'invite_link';
  directAccess?: boolean;
  isActive?: boolean;
  requiresTemporaryPasswordLogin?: boolean;
  loginUrl?: string;
  createdAt?: string;
  expiresAt?: string;
  inviteUrl?: string;
}

export interface ChangeRoleDto {
  role: string;
}

// Notifications: types moved to @/services/notificationsService
// (AssetNotification) — case-management-service no longer has its own
// notifications REST API.

// Audit: types moved to @/services/auditService (AuditLogRecord) —
// case-management-service no longer has its own audit REST API.

// ── Plans Types (matches backend plans.catalog.ts) ──────────────────────────

export interface PlanModuleDef {
  available: boolean;
  included: boolean;
  costUSD: number;
  costETB: number;
}

export interface PlanDefinition {
  name: string;
  displayName: string;
  scope: string;
  summary: string;
  priceNote?: string;
  trialDays?: number;
  limits: {
    maxUsers: number | null;
    includedUsers: number;
    extraUserCostUSD: number;
    extraUserCostETB: number;
    maxCases: number | null;
    includedCasesPerMonth: number;
    caseOveragePer100USD: number;
    caseOveragePer100ETB: number;
    caseLimitPeriod: string;
  };
  modules: {
    integration: PlanModuleDef;
    soar: PlanModuleDef;
  };
  pricing: {
    platformFeeUSD: number;
    platformFeeETB: number;
    currency: 'USD' | 'ETB';
    exchangeRate: number;
  };
  support: string;
  allowedRoles?: string[];
  features: string[];
  notes?: string[];
}

export interface TrialStatus {
  isTrial: boolean;
  trialActivated: boolean;  // false = user hasn't clicked "Start Free Trial" yet
  startDate?: string | null;
  endDate?: string | null;
  isExpired?: boolean;
  daysRemaining?: number | null;
}

export interface CurrentPlanResponse {
  organizationId: string | null;
  plan: PlanDefinition | null;
  organization: Record<string, any> | null;
  trial: TrialStatus | null;
  upgradeRequest?: SubscriptionRequest | null;
  requestHistory?: any[];
  /** Which billing path applies. One CMS bundle serves on-premise and SaaS. */
  deployment?: DeploymentKind;
  /** On-premise only. */
  license?: LicenseStatusResponse;
}


// ── Pricing Calculator Types ─────────────────────────────────────────────────

export interface CalculatePricingDto {
  planName: string;
  users: number;
  casesPerMonth: number;
  addIntegration?: boolean;
  addSoar?: boolean;
  contractYears: number;
  currency?: 'USD' | 'ETB';
}

export interface PricingBreakdown {
  planName: string;
  planDisplayName: string;
  currency: 'USD' | 'ETB';
  exchangeRate: number;
  inputs: {
    users: number;
    casesPerMonth: number;
    addIntegration: boolean;
    addSoar: boolean;
    contractYears: number;
  };
  limits: {
    includedUsers: number;
    maxUsers: number | null;
    extraUsers: number;
    includedCasesPerMonth: number;
    overageCases: number;
  };
  modules: {
    integration: { available: boolean; included: boolean; selected: boolean; cost: number };
    soar: { available: boolean; included: boolean; selected: boolean; cost: number };
  };
  costs: {
    platformFee: number;
    extraUsersCost: number;
    caseOverageCost: number;
    integrationModuleCost: number;
    soarModuleCost: number;
    subtotal: number;
  };
  pricing: {
    yearOneARR: number;
    contractDiscount: number;
    contractDiscountAmount: number;
    totalContractValue: number;
    averageAnnualCost: number;
  };
  warnings: string[];
  notes: string[];
}

// ── Upgrade Request Types ────────────────────────────────────────────────────

export interface RequestUpgradeDto {
  planName: string;
  users: number;
  casesPerMonth: number;
  addIntegration?: boolean;
  addSoar?: boolean;
  contractYears: number;
  currency?: 'USD' | 'ETB';
  notes?: string;
}

export interface UpgradeRequestResponse {
  message: string;
  requestId?: string;
  /** On-premise only: the file to send Kibo to get a license. */
  requestFile?: { fileName: string; content: string };
  bankDetails: {
    bankName: string;
    accountName: string;
    accountNumber: string;
    swiftCode: string;
    instructions: string;
  };
  calculation: PricingBreakdown;
}


export interface SubscriptionRequest {
  id: string;
  organizationId: string;
  planName: string;
  users: number;
  casesPerMonth: number;
  addIntegration: boolean;
  addSoar: boolean;
  contractYears: number;
  currency: string;
  totalContractValue: number;
  notes?: string | null;
  status: string;
  rejectionReason?: string | null;
  createdAt: string;
  updatedAt: string;
  organization?: { id: string; name: string; slug: string } | null;
}

export interface SubscribedOrganization {
  id: string;
  name: string;
  slug: string;
  status: string;
  sector?: string | null;
  subscriptionPlan: string;
  trialStartDate?: string | null;
  trialEndDate?: string | null;
  maxUsers: number;
  maxCases: number;
  maxIntegrations: number;
  supportLevel: string;
  apiAccessEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PlanPricingConfig {
  usdToEtbRate: number;
  updatedAt: string;
  plans: PlanPriceUsd[];
}

export interface PlanPriceUsd {
  planName: string;
  platformFeeUsd: number;
  includedUsers: number;
  extraUserCostUsd: number;
  includedCasesPerMonth: number;
  caseOveragePer100Usd: number;
  integrationCostUsd: number;
  soarCostUsd: number;
  contractDiscount2Yr: number;
  contractDiscount3Yr: number;
  updatedBySub?: string | null;
  updatedByEmail?: string | null;
  updatedAt?: string;
  createdAt?: string;
}

// ── Tenant administration (hosted deployments, platform_admin only) ──────────

/** The intake form, as submitted. Display-only for the reviewing operator. */
export interface TenantRegistrationDetails {
  sector?: string;
  industry?: string;
  organizationSize?: string;
  website?: string;
  logo?: string;
  country?: string;
  city?: string;
  address?: string;
  phone?: string;
  contactPerson?: string;
  contactRole?: string;
  alternateContactName?: string;
  alternateContactRole?: string;
  alternateContactEmail?: string;
  description?: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'PENDING' | 'ACTIVE' | 'REJECTED' | 'SUSPENDED' | 'INACTIVE';
  subscriptionPlan: string;
  contactEmail: string | null;
  registrationDetails: TenantRegistrationDetails | null;
  rejectionReason: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  trialStartDate: string | null;
  trialEndDate: string | null;
  maxUsers: number;
  maxCases: number;
  createdAt: string;
  /** Present only on the organizations listing, which counts across tenants. */
  userCount?: number;
  caseCount?: number;
}

export interface PlanPricingSchema {
  planName: string;
  editableFields: Record<string, boolean>;
  limits: Record<string, { min: number; max: number }>;
  notes: string[];
}


